import { toQueryParams } from '@/helpers/ApiHelper'
import type { IIdmPaginatedResponse } from '@/misc/IIdmPaginatedResponse'
import type { IIdmSorting } from '@/models/Filtering/IIdmSorting'
import type { IFleet } from '@/models/IFleet'
import type { IGroupFilter } from '@/models/UserManagement/Filtering/IGroupFilter'
import type { IOrganisationFilter } from '@/models/UserManagement/Filtering/IOrganisation'
import type { IPermissionFilter } from '@/models/UserManagement/Filtering/IPermissionFilter'
import type { IRoleFilter } from '@/models/UserManagement/Filtering/IRoleFilter'
import type { IUsersFilter } from '@/models/UserManagement/Filtering/IUsersFilter'
import type { IContract } from '@/models/UserManagement/IContract'
import type { ICPO } from '@/models/UserManagement/ICPO'
import type { IGroup } from '@/models/UserManagement/IGroup'
import type { IOrganisation } from '@/models/UserManagement/IOrganisation'
import type { IPermission } from '@/models/UserManagement/IPermission'
import type { IRole } from '@/models/UserManagement/IRole'
import type { IUMProject } from '@/models/UserManagement/IUMProject'
import type { IUser } from '@/models/UserManagement/IUser'
import { inject, injectable } from 'inversify'
import type IApiClient from '../IApiClient'
import type { IApiRequestOptions } from '../IApiRequestOptions'
import { SERVICES_TYPES } from '../ServicesTypes'
import type IUserManagementService from './IUserManagementService'

@injectable()
export class UserManagementService implements IUserManagementService {
  private readonly apiClient: IApiClient

  constructor(@inject(SERVICES_TYPES.ApiClient) apiClient: IApiClient) {
    this.apiClient = apiClient
  }

  public async getAllUsersPaged(page: number, pageSize: number, filters: IUsersFilter, sorting: IIdmSorting): Promise<IIdmPaginatedResponse<IUser>> {
    return this.apiClient.get([`users/paged?pageIndex=${page}&pageSize=${pageSize}`, toQueryParams(filters), toQueryParams(sorting)].filter(Boolean).join('&'))
  }

  public async getUser(id: string, abortController?: AbortController, options?: IApiRequestOptions): Promise<IUser> {
    return this.apiClient.get(`users/${id}`, abortController, options)
  }

  public async addUser(user: IUser): Promise<IUser> {
    return this.apiClient.post(`users`, user)
  }

  public async updateUser(user: IUser): Promise<IUser> {
    return this.apiClient.put(`users/${user.Id}`, user)
  }

  resendInvitation(userId: string): Promise<void> {
    return this.apiClient.post(`users/${userId}/resend-invitation`, {})
  }

  hardDeleteUser(userId: string): Promise<void> {
    return this.apiClient.delete(`users/hard-delete/${userId}`)
  }

  public async getAllPermissionsPaged(page: number, pageSize: number, filters: IPermissionFilter, sorting: IIdmSorting): Promise<IIdmPaginatedResponse<IPermission>> {
    return this.apiClient.get([`permissions/paged?pageIndex=${page}&pageSize=${pageSize}`, toQueryParams(filters), toQueryParams(sorting)].filter(Boolean).join('&'))
  }

  public async getAllPermissions(): Promise<IPermission[]> {
    return this.apiClient.get('permissions')
  }

  public async getPermission(id: string, abortController?: AbortController, options?: IApiRequestOptions): Promise<IPermission[]> {
    return this.apiClient.get(`permissions?id=${id}`, abortController, options)
  }
  public async updatePermission(permission: IPermission): Promise<IPermission> {
    return this.apiClient.put(`permissions/${permission.Id}`, permission)
  }

  public async addPermission(permission: IPermission): Promise<IPermission> {
    return this.apiClient.post(`permissions`, permission)
  }

  public async getAllGroupsPaged(page: number, pageSize: number, filters: IGroupFilter, sorting: IIdmSorting): Promise<IIdmPaginatedResponse<IGroup>> {
    return this.apiClient.get([`groups/paged?pageIndex=${page}&pageSize=${pageSize}`, toQueryParams(filters), toQueryParams(sorting)].filter(Boolean).join('&'))
  }

  public async getAllGroups(): Promise<IGroup[]> {
    return this.apiClient.get('groups')
  }

  public async getGroup(id: string, abortController?: AbortController, options?: IApiRequestOptions): Promise<IGroup> {
    return this.apiClient.get(`groups/${id}`, abortController, options)
  }

  public async getAllRolesPaged(page: number, pageSize: number, filters: IRoleFilter, sorting: IIdmSorting): Promise<IIdmPaginatedResponse<IRole>> {
    return this.apiClient.get([`roles/paged?pageIndex=${page}&pageSize=${pageSize}`, toQueryParams(filters), toQueryParams(sorting)].filter(Boolean).join('&'))
  }

  public async getAllRoles(): Promise<IRole[]> {
    return this.apiClient.get(`roles`)
  }

  public async getRole(id: string, abortController?: AbortController, options?: IApiRequestOptions): Promise<IRole> {
    return this.apiClient.get(`roles/${id}`, abortController, options)
  }

  public async addRole(role: IRole): Promise<IRole> {
    return this.apiClient.post(`roles`, role)
  }

  public async updateRole(role: IRole): Promise<IRole> {
    return this.apiClient.put(`roles/${role.Id}`, role)
  }

  public async getAllOrganisationsPaged(page: number, pageSize: number, filters: IOrganisationFilter, sorting: IIdmSorting): Promise<IIdmPaginatedResponse<IOrganisation>> {
    return this.apiClient.get([`organisations/paged?pageIndex=${page}&pageSize=${pageSize}`, toQueryParams(filters), toQueryParams(sorting)].filter(Boolean).join('&'))
  }

  public async getAllOrganisations(): Promise<IOrganisation[]> {
    return this.apiClient.get('organisations')
  }

  public async getOrganisation(id: string, abortController?: AbortController, options?: IApiRequestOptions): Promise<IOrganisation> {
    return this.apiClient.get(`organisations/${id}`, abortController, options)
  }

  public async getAllCPOs(): Promise<ICPO[]> {
    return this.apiClient.get('cpos')
  }

  public async getAllProjects(): Promise<IUMProject[]> {
    return this.apiClient.get('projects')
  }

  public async getAllContracts(): Promise<IContract[]> {
    return this.apiClient.get('contracts')
  }

  public async getAllFleets(): Promise<IFleet[]> {
    return this.apiClient.get('fleets')
  }
}
